// Karten einer Seite neu anordnen -- auf JEDER Seite, ohne dass die Seite
// etwas dafür tut (Tim 16.10.2026: „die Karten auf der Seite so bauen,
// dass diese neu angeordnet werden können. Das gilt für alle Seiten.").
//
// Aufbau:
//   - Der Wirt jeder Seite (AnordnungsWirt, aus PageHost bzw. App am
//     Telefon) legt EINE Anordnung in den Kontext.
//   - Jede Karte meldet sich per use:anordenbar an. DataTableSection tut
//     das von selbst (Kennung = collapseKey), handgebaute Karten mit einer
//     Zeile im Markup. Gruppe SEITE = die Karten der Seite; eigene Gruppen
//     ordnen Dinge INNERHALB einer Karte (Server: Kennzahlen).
//   - Gedrückt halten öffnet den Sortiermodus (press-reorder.ts, wie auf
//     Dashboard und Backups), gezogen wird live.
//
// Warum CSS order statt Umsortieren im Markup: die Seiten sind statisches
// Markup, keine Liste, die man umsortiert ausgeben könnte. Jede Seite in
// Snippets zu zerlegen hieße, 50 Seiten umzubauen -- und jede neue müsste
// daran denken. order ordnet nur die Darstellung; die Seite selbst bleibt,
// wie sie ist. Preis: die Tab-Reihenfolge folgt weiter dem Markup.
//
// Gespeichert wird die Reihenfolge je Seite in den Prefs (Server, alle
// Geräte, live per WS) unter card_order_<gruppe> -- derselbe Schlüssel
// wie bei den Backup-Gruppen und den Karten der Sensoren.

import { getContext, hasContext, setContext, tick } from "svelte";

import { ordne, ordnungsWerte, verschiebe } from "./anordnung";
import { getPref, setPref } from "./prefs.svelte";
import { pressReorder, scrollFlaeche, type PressReorderParams } from "./press-reorder";

/** Gruppe der Karten einer Seite. */
export const SEITE = "seite";

const KONTEXT = Symbol.for("anordnung");

export interface AnordenbarParams {
  /** die Anordnung aus dem Kontext (anordnung()); ohne Wirt null -- dann
   *  ist die Karte einfach nicht verschiebbar */
  ordnung: Anordnung | null;
  /** stabile Kennung innerhalb der Gruppe (wird gespeichert) */
  id: string;
  /** Standard SEITE */
  gruppe?: string;
  /** Nur ein Druck hier (CSS-Selektor) sortiert, siehe press-reorder.ts */
  griff?: string;
  /** Im Sortiermodus nur das erste Kind (den Kopf) zeigen. Für
   *  handgebaute Karten; DataTableSection klappt sich selbst zusammen. */
  flach?: boolean;
  /** Satz in der Leiste des Sortiermodus („Kennzahlen ziehen …") */
  hinweis?: string;
  /** Die Karte zeigt die Griff-Punkte im Modus selbst (in ihrem Kopf,
   *  statt des Pfeils). Sonst malt app.css sie in die linke obere Ecke. */
  punkteSelbst?: boolean;
}

interface Glied {
  el: HTMLElement;
  p: AnordenbarParams;
  zug: { update(p: PressReorderParams): void; destroy(): void };
}

export class Anordnung {
  /** Gruppe im Sortiermodus, sonst null */
  sortiert = $state<string | null>(null);

  #seite: () => string;
  #glieder = $state.raw<Glied[]>([]);
  // Zählt Änderungen an den Behältern (Kinder kommen und gehen), damit
  // die Werte neu verteilt werden -- etwa ein Hinweis, der erst nach dem
  // Laden erscheint und sonst mit order 0 nach oben spränge.
  #takt = $state(0);
  #nr = new WeakMap<Element, number>();
  #naechsteNr = 1;
  // Was wir mit order versehen haben, und welcher Wert dort stehen soll.
  // Schreibt Svelte das style-Attribut eines Elements neu, stellt der
  // Beobachter den Wert wieder her.
  #erwartet = new Map<HTMLElement, string>();
  #beobachter: MutationObserver | null = null;
  #anker: HTMLElement | null = null;

  constructor(seite: () => string) {
    this.#seite = seite;
    if (typeof MutationObserver !== "undefined") {
      this.#beobachter = new MutationObserver((liste) => this.#geaendert(liste));
    }
    $effect(() => this.#verteile());
    $effect(() => this.#zuegeStellen());
    $effect(() => this.#modusHalten());
    $effect(() => () => {
      this.#beobachter?.disconnect();
      for (const el of this.#erwartet.keys()) el.style.order = "";
    });
  }

  /** Prefs-Schlüssel der Gruppe. Die Karten einer Seite gehören der
   *  Route (dem Muster, nicht dem Pfad: alle Host-Seiten teilen eine
   *  Ordnung). */
  schluessel(gruppe: string): string {
    return gruppe === SEITE ? `card_order_seite:${this.#seite()}` : `card_order_${gruppe}`;
  }

  /** Weicht die Gruppe von der Reihenfolge im Markup ab? */
  angepasst(gruppe: string): boolean {
    return (getPref<string[]>(this.schluessel(gruppe))?.length ?? 0) > 0;
  }

  hinweis(gruppe: string): string | undefined {
    return this.#glieder.find((g) => (g.p.gruppe ?? SEITE) === gruppe && g.p.hinweis)?.p.hinweis;
  }

  /** Sortiermodus öffnen. el = die gedrückte Karte: sie bleibt unter dem
   *  Finger, auch wenn die Karten darüber sich zusammenlegen. */
  beginne(gruppe: string, el: HTMLElement): void {
    if (this.sortiert === gruppe) return;
    this.#anker = el;
    this.#halteFest(el, () => (this.sortiert = gruppe));
  }

  beende(): void {
    if (!this.sortiert) return;
    const el = this.#anker;
    this.#anker = null;
    if (el?.isConnected) this.#halteFest(el, () => (this.sortiert = null));
    else this.sortiert = null;
  }

  zuruecksetzen(gruppe: string): void {
    setPref(this.schluessel(gruppe), null);
  }

  verschiebe(gruppe: string, von: string, nach: string): void {
    const key = this.schluessel(gruppe);
    const gespeichert = getPref<string[]>(key);
    const aktuell = ordne(gespeichert, this.#standard(gruppe).map((id) => ({ id }))).map((x) => x.id);
    const neu = verschiebe(gespeichert, aktuell, von, nach);
    if (neu) setPref(key, neu);
  }

  /** Von use:anordenbar gerufen. Gibt das Abmelden zurück. */
  melde(el: HTMLElement, p: AnordenbarParams): () => void {
    const g: Glied = { el, p, zug: { update() {}, destroy() {} } };
    this.#glieder = [...this.#glieder, g];
    g.zug = pressReorder(el, this.#zugParams(g));
    return () => {
      g.zug.destroy();
      this.#glieder = this.#glieder.filter((x) => x !== g);
      delete el.dataset.anordnung;
      delete el.dataset.anordnungFlach;
      delete el.dataset.anordnungPunkte;
      if (this.sortiert && !this.#glieder.some((x) => (x.p.gruppe ?? SEITE) === this.sortiert)) this.sortiert = null;
    };
  }

  /** Neue Angaben einer Karte (id und Gruppe bleiben, siehe Aktion). */
  aendere(el: HTMLElement, p: AnordenbarParams): void {
    const g = this.#glieder.find((x) => x.el === el);
    if (!g) return;
    g.p = p;
    g.zug.update(this.#zugParams(g));
  }

  // ── intern ───────────────────────────────────────────────────────

  // Kennungen der Gruppe in Markup-Reihenfolge -- die DOM-Reihenfolge
  // ändert sich nie (order ordnet nur die Darstellung).
  #standard(gruppe: string): string[] {
    return this.#glieder
      .filter((g) => (g.p.gruppe ?? SEITE) === gruppe)
      .map((g) => g.el)
      .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
      .map((el) => this.#glieder.find((g) => g.el === el)!.p.id);
  }

  #behaelterNr(el: Element | null): number {
    if (!el) return 0;
    let n = this.#nr.get(el);
    if (n === undefined) {
      n = this.#naechsteNr++;
      this.#nr.set(el, n);
    }
    return n;
  }

  #zugParams(g: Glied): PressReorderParams {
    const gruppe = g.p.gruppe ?? SEITE;
    const behaelter = g.el.parentElement;
    // Gezogen wird nur unter Geschwistern: Karten in einem anderen
    // Behälter (Docker: je Rechner ein Block) sind kein Ziel.
    const nachbarn = this.#glieder.filter((x) => (x.p.gruppe ?? SEITE) === gruppe && x.el.parentElement === behaelter);
    return {
      group: `${gruppe}~${this.#behaelterNr(behaelter)}`,
      id: g.p.id,
      armed: this.sortiert === gruppe,
      aus: nachbarn.length < 2,
      griff: g.p.griff,
      onActivate: () => this.beginne(gruppe, g.el),
      onReorder: (_z, von, nach) => this.verschiebe(gruppe, von, nach),
      liftClass: "anordnung-zieht",
    };
  }

  // Sortiermodus und Einzahl an jede Karte weitergeben.
  #zuegeStellen(): void {
    const sortiert = this.sortiert;
    for (const g of this.#glieder) {
      g.zug.update(this.#zugParams(g));
      const an = sortiert !== null && sortiert === (g.p.gruppe ?? SEITE);
      if (an) g.el.dataset.anordnung = "an";
      else delete g.el.dataset.anordnung;
      if (an && g.p.flach) g.el.dataset.anordnungFlach = "";
      else delete g.el.dataset.anordnungFlach;
      if (an && !g.p.punkteSelbst) g.el.dataset.anordnungPunkte = "";
      else delete g.el.dataset.anordnungPunkte;
    }
  }

  // order-Werte verteilen: je Gruppe mit eigener Reihenfolge, je Behälter.
  #verteile(): void {
    void this.#takt;
    const nachGruppe = new Map<string, Glied[]>();
    for (const g of this.#glieder) {
      const k = g.p.gruppe ?? SEITE;
      nachGruppe.set(k, [...(nachGruppe.get(k) ?? []), g]);
    }
    const jetzt = new Map<HTMLElement, string>();
    for (const [gruppe, liste] of nachGruppe) {
      const gespeichert = getPref<string[]>(this.schluessel(gruppe));
      if (!gespeichert?.length) continue;
      const reihe = ordne(gespeichert, this.#standard(gruppe).map((id) => ({ id })));
      const rang = new Map(reihe.map((x, i) => [x.id, i]));
      const behaelter = new Set(liste.map((g) => g.el.parentElement).filter((b): b is HTMLElement => !!b));
      for (const b of behaelter) {
        const kinder = [...b.children] as HTMLElement[];
        const ids = kinder.map((k) => liste.find((g) => g.el === k)?.p.id ?? null);
        ordnungsWerte(ids, rang).forEach((w, i) => jetzt.set(kinder[i], w === 0 ? "" : String(w)));
      }
    }
    for (const [el] of this.#erwartet) if (!jetzt.has(el) && el.style.order) el.style.order = "";
    for (const [el, w] of jetzt) if (el.style.order !== w) el.style.order = w;
    this.#erwartet = jetzt;

    // Beobachten, solange etwas angeordnet ist: neue Kinder der Behälter
    // und das style-Attribut der Kinder.
    const b = this.#beobachter;
    if (!b) return;
    b.disconnect();
    const behaelter = new Set<Element>();
    for (const el of jetzt.keys()) {
      b.observe(el, { attributes: true, attributeFilter: ["style"] });
      if (el.parentElement) behaelter.add(el.parentElement);
    }
    for (const x of behaelter) b.observe(x, { childList: true });
  }

  #geaendert(liste: MutationRecord[]): void {
    let neu = false;
    for (const m of liste) {
      if (m.type === "childList") neu = true;
      else {
        const el = m.target as HTMLElement;
        const w = this.#erwartet.get(el);
        if (w !== undefined && el.style.order !== w) el.style.order = w;
      }
    }
    if (neu) this.#takt++;
  }

  #modusHalten(): (() => void) | undefined {
    const gruppe = this.sortiert;
    if (!gruppe) return;
    return sortierModusHalten(`[data-reorder-group^="${gruppe}~"]`, () => this.beende());
  }

  // Zustand wechseln und dabei el an seinem Platz im Fenster halten: legen
  // sich die Karten darüber zusammen (oder wieder auf), scrollt die Fläche
  // um genau den Sprung nach.
  #halteFest(el: HTMLElement, wechsel: () => void): void {
    const vorher = el.getBoundingClientRect().top;
    wechsel();
    void tick().then(() => {
      if (!el.isConnected) return;
      const sprung = el.getBoundingClientRect().top - vorher;
      const s = scrollFlaeche(el);
      if (s && Math.abs(sprung) > 1) s.scrollTop += sprung;
    });
  }
}

/** Sortiermodus offen halten, bis daneben gedrückt wird oder Escape kommt
 *  -- wie auf dem Dashboard. `dazu` = Selektor der Dinge, die zum Modus
 *  gehören; die Leiste ist per data-arrange-keep ausgenommen, sonst
 *  schlösse der pointerdown den Modus, bevor ihr Klick ankommt. Gibt das
 *  Aufräumen zurück (für $effect). Auch vom Baum genutzt (shell/tree). */
export function sortierModusHalten(dazu: string, beende: () => void): () => void {
  const onDown = (e: PointerEvent) => {
    const el = e.target as HTMLElement | null;
    if (el?.closest(`${dazu},[data-arrange-keep]`)) return;
    beende();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") beende();
  };
  document.addEventListener("pointerdown", onDown, true);
  document.addEventListener("keydown", onKey);
  return () => {
    document.removeEventListener("pointerdown", onDown, true);
    document.removeEventListener("keydown", onKey);
  };
}

/** Vom Wirt gerufen, einmal je Seite. */
export function setAnordnung(a: Anordnung): void {
  setContext(KONTEXT, a);
}

/** In der Initialisierung einer Komponente rufen; ohne Wirt null. */
export function anordnung(): Anordnung | null {
  return hasContext(KONTEXT) ? getContext<Anordnung>(KONTEXT) : null;
}

/** Svelte-Aktion: das Element ist eine Karte seiner Gruppe. */
export function anordenbar(node: HTMLElement, params: AnordenbarParams) {
  let p = params;
  let ab = p.ordnung?.melde(node, p) ?? null;
  return {
    update(next: AnordenbarParams) {
      const neu = next.ordnung !== p.ordnung || next.id !== p.id || (next.gruppe ?? SEITE) !== (p.gruppe ?? SEITE);
      p = next;
      if (neu) {
        ab?.();
        ab = p.ordnung?.melde(node, p) ?? null;
      } else {
        p.ordnung?.aendere(node, p);
      }
    },
    destroy() {
      ab?.();
    },
  };
}
